import { Injectable, inject } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  GET_CONVERSION_FACTOR,
  CONVERT_QUANTITY,
  GET_UNIT_CONVERSIONS_BY_UNIT,
  GET_ALL_UNIT_CONVERSIONS,
  CREATE_UNIT_CONVERSION,
  GET_AVAILABLE_CONVERSIONS_FROM_UNIT,
} from '../../@graphql/operations/unitConversionQueries';
import { IUnitConversion } from '../../@interfaces/unit-conversion.interface';
import { IUnits } from '../../@interfaces/units.interface';
import { CookieAuthService } from '../storage-variables/cookie-auth.service';

@Injectable({
  providedIn: 'root',
})
export class UnitConversionService {
  private readonly cookieAuthService = inject(CookieAuthService);

  constructor(private apollo: Apollo) {}

  getConversionFactor(
    fromUnit: number,
    toUnit: number,
  ): Observable<number | null> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.apollo
      .query<{ getConversionFactor: number | null }>({
        query: GET_CONVERSION_FACTOR,
        variables: {
          fromUnit: fromUnit.toString(),
          toUnit: toUnit.toString(),
          company: company?.toString(),
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          // Handle null or undefined response from backend
          if (
            result.data?.getConversionFactor === null ||
            result.data?.getConversionFactor === undefined
          ) {
            return null;
          }
          return result.data.getConversionFactor;
        }),
        catchError((error) => {
          console.error(
            '[UnitConversionService] Error getting conversion factor:',
            error,
          );
          // Return null instead of throwing error to allow graceful handling
          return of(null);
        }),
      );
  }

  getConversionsByUnit(unitId: number): Observable<IUnitConversion[]> {
    return this.apollo
      .query<{ getUnitConversionsByUnit: IUnitConversion[] }>({
        query: GET_UNIT_CONVERSIONS_BY_UNIT,
        variables: { idUnit: unitId.toString() },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data.getUnitConversionsByUnit),
        catchError(() => of([])),
      );
  }

  /**
   * Gets all available units that have conversions registered from a specific unit
   * This returns only units that can be converted from the source unit
   */
  getAvailableConversionsFromUnit(
    unitId: number,
  ): Observable<{ unit: IUnits; conversion_factor: number }[]> {
    const companyObject = this.cookieAuthService.getCompanyObject;
    const company = companyObject?.Id_company;

    return this.apollo
      .query<{
        getAvailableConversionsFromUnit: {
          unit: IUnits;
          conversion_factor: number;
        }[];
      }>({
        query: GET_AVAILABLE_CONVERSIONS_FROM_UNIT,
        variables: {
          idUnit: unitId.toString(),
          company: company?.toString(),
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data.getAvailableConversionsFromUnit),
        catchError((error) => {
          console.error(
            '[UnitConversionService] Error getting available conversions:',
            error,
          );
          return of([]);
        }),
      );
  }

  getAllUnitConversions(): Observable<IUnitConversion[]> {
    return this.apollo
      .query<{
        getAllUnitConversions: {
          conversions: IUnitConversion[];
          count: number;
        };
      }>({
        query: GET_ALL_UNIT_CONVERSIONS,
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => result.data.getAllUnitConversions.conversions),
        catchError(() => of([])),
      );
  }

  createConversion(
    fromUnit: number,
    toUnit: number,
    conversionFactor: number,
    description?: string,
    isBidirectional: boolean = true,
  ): Observable<IUnitConversion | null> {
    return this.apollo
      .mutate<{ createUnitConversion: IUnitConversion }>({
        mutation: CREATE_UNIT_CONVERSION,
        variables: {
          fromUnit: fromUnit.toString(),
          toUnit: toUnit.toString(),
          conversionFactor,
          description,
          isBidirectional,
        },
      })
      .pipe(
        map((result) => result.data?.createUnitConversion || null),
        catchError(() => of(null)),
      );
  }

  async convertQuantity(
    quantity: number,
    fromUnit: number,
    toUnit: number,
    customFactor?: number,
  ): Promise<number> {
    if (fromUnit === toUnit) {
      return quantity;
    }

    if (
      customFactor !== undefined &&
      customFactor !== null &&
      customFactor > 0
    ) {
      return quantity * customFactor;
    }

    try {
      const result = await this.apollo
        .query<{ convertQuantity: { converted_quantity: number } }>({
          query: CONVERT_QUANTITY,
          variables: {
            fromUnit: fromUnit.toString(),
            toUnit: toUnit.toString(),
            quantity,
          },
          fetchPolicy: 'network-only',
        })
        .toPromise();

      if (result?.data?.convertQuantity?.converted_quantity) {
        return result.data.convertQuantity.converted_quantity;
      }
      return quantity;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Calculates the inverse conversion factor for bidirectional conversions
   */
  getInverseFactor(factor: number): number {
    if (factor === 0) return 0;
    return 1 / factor;
  }

  /**
   * Validates if there is a conversion between two units
   */
  async hasConversion(fromUnit: number, toUnit: number): Promise<boolean> {
    if (fromUnit === toUnit) return true;

    // Query backend to check if conversion existsss
    try {
      const factor = await this.getConversionFactor(
        fromUnit,
        toUnit,
      ).toPromise();
      return factor !== null && factor !== undefined;
    } catch {
      return false;
    }
  }
}
